import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from '../components/ui/select';
import { toast } from '../components/ui/use-toast';
import { useAppSettings } from '../context/AppSettingsContext';
import { useT } from '../context/LanguageContext';
import { mastersService } from '../services/mastersService';
import { useModuleAccess } from '../utils/useModuleAccess';
import AccessDenied from '../components/AccessDenied';
import {
  formatDateWith, formatTimeWith, formatDateTimeWith,
} from '../utils/dateFormat';

// --- timezone helpers ------------------------------------------------------

// Offset label, minutes and a search string for an IANA timezone, covering every offset form a user might type.
function enrichTimezone(tz) {
  let offsetMinutes = 0;
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'longOffset',
    }).formatToParts(new Date());
    const raw = parts.find((p) => p.type === 'timeZoneName')?.value || 'GMT';
    const m = raw.match(/GMT([+-])(\d{2}):(\d{2})/);
    if (m) {
      const [, sign, hh, mm] = m;
      offsetMinutes = (Number(hh) * 60 + Number(mm)) * (sign === '+' ? 1 : -1);
    }
  } catch { /* ignore unknown zones */ }

  const sign = offsetMinutes >= 0 ? '+' : '-';
  const abs = Math.abs(offsetMinutes);
  const h = Math.floor(abs / 60);
  const mins = abs % 60;
  const hh = String(h).padStart(2, '0');
  const mm = String(mins).padStart(2, '0');
  const decimal = h + mins / 60;
  // "+5", "+5.5", "+5.75", "-8"
  const decimalStr = `${sign}${
    decimal === Math.floor(decimal)
      ? decimal
      : String(decimal).replace(/0+$/, '').replace(/\.$/, '')
  }`;
  const offsetLabel = offsetMinutes === 0 ? 'UTC' : `UTC${sign}${hh}:${mm}`;

  const search = [
    tz.toLowerCase(),
    offsetLabel.toLowerCase(),
    `${sign}${hh}:${mm}`,    // "+05:30"
    `${sign}${h}:${mm}`,     // "+5:30"
    decimalStr.toLowerCase(),// "+5.5"
    `gmt${sign}${hh}:${mm}`, // "gmt+05:30"
  ].join(' ');

  return { offsetLabel, offsetMinutes, search };
}

function SearchableTimezoneSelect({ value, onChange, options, placeholder }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const enriched = useMemo(
    () => options.map((tz) => ({ tz, ...enrichTimezone(tz) })),
    [options]
  );

  // Sort by offset (negative → positive), then name. Keeps similar zones together.
  const sorted = useMemo(
    () => [...enriched].sort((a, b) => a.offsetMinutes - b.offsetMinutes || a.tz.localeCompare(b.tz)),
    [enriched]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter((x) => x.search.includes(q));
  }, [sorted, query]);

  const current = enriched.find((x) => x.tz === value);
  const displayValue = open
    ? query
    : current
      ? `${current.tz} (${current.offsetLabel})`
      : value || '';

  return (
    <div className="relative" ref={rootRef}>
      <Input
        value={displayValue}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => { setQuery(''); setOpen(true); }}
        placeholder={placeholder}
        autoComplete="off"
      />
      {open && (
        <div className="absolute z-50 mt-1 w-full max-h-72 overflow-auto rounded-md border bg-popover shadow-md">
          {filtered.length === 0 ? (
            <div className="p-3 text-sm text-muted-foreground">No matches</div>
          ) : filtered.map((opt) => (
            <button
              key={opt.tz}
              type="button"
              onClick={() => { onChange(opt.tz); setOpen(false); setQuery(''); }}
              className={`w-full text-left px-3 py-1.5 text-sm flex items-center justify-between gap-3 hover:bg-accent ${
                opt.tz === value ? 'bg-accent/50 font-medium' : ''
              }`}
            >
              <span>{opt.tz}</span>
              <span className="text-muted-foreground font-mono text-xs">{opt.offsetLabel}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// --- page ------------------------------------------------------------------

export default function AppPreferences() {
  const { settings, loaded, update, refresh } = useAppSettings();
  const { t } = useT();
  const { canView, canEdit } = useModuleAccess('data_display');

  const [draft, setDraft] = useState({
    timezone: 'UTC',
    date_format: 'DD/MM/YYYY',
    time_format: 'HH:mm',
  });

  const [timezones, setTimezones] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const tz = await mastersService.timezones();
        setTimezones(tz);
      } catch (e) {
        toast({ title: t('dataDisplay.loadOptionsFailed'), description: String(e?.message || e) });
      }
    })();
  }, [t]);

  useEffect(() => {
    if (!loaded) return;
    setDraft({
      timezone: settings.timezone || 'UTC',
      date_format: settings.date_format || 'DD/MM/YYYY',
      time_format: settings.time_format || 'HH:mm',
    });
  }, [loaded, settings]);

  const dateChoices = settings.date_format_choices?.length
    ? settings.date_format_choices
    : [
        { value: 'DD/MM/YYYY', label: '31/12/2026' },
        { value: 'MM/DD/YYYY', label: '12/31/2026' },
        { value: 'YYYY-MM-DD', label: '2026-12-31' },
        { value: 'DD-MM-YYYY', label: '31-12-2026' },
        { value: 'DD MMM YYYY', label: '31 Dec 2026' },
        { value: 'MMM DD, YYYY', label: 'Dec 31, 2026' },
      ];

  const timeChoices = settings.time_format_choices?.length
    ? settings.time_format_choices
    : [
        { value: 'HH:mm', label: '24-hour (14:30)' },
        { value: 'HH:mm:ss', label: '24-hour with seconds (14:30:00)' },
        { value: 'hh:mm A', label: '12-hour (02:30 PM)' },
        { value: 'hh:mm:ss A', label: '12-hour with seconds (02:30:00 PM)' },
      ];

  // Re-derive "now" whenever the user touches the draft so the format preview
  // ticks forward — `draft` is the trigger, not a data dependency.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const now = useMemo(() => new Date(), [draft]);

  const onSave = async () => {
    setSaving(true);
    try {
      await update({
        timezone: draft.timezone,
        date_format: draft.date_format,
        time_format: draft.time_format,
      });
      toast({ title: t('common.saved'), description: t('common.savedDescription') });
      await refresh();
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || t('common.saveFailed');
      toast({ title: t('common.saveFailed'), description: msg });
    } finally {
      setSaving(false);
    }
  };

  if (!canView) {
    return <AccessDenied perm="data_display.view" what="Data Display preferences" />;
  }

  return (
    <fieldset disabled={!canEdit} className="border-0 p-0 m-0">
    <div className="p-6 space-y-6">
      <div>
        <h1 className="font-display text-3xl font-semibold text-foreground tracking-tight leading-none">{t('dataDisplay.title')}</h1>
      </div>
      {!canEdit && (
        <div className="rounded-md border border-warning/30 bg-warning-soft px-4 py-2.5 text-sm text-warning">
          <strong>Read-only access.</strong> Your role can view these preferences but cannot change them.
        </div>
      )}

      {/* Card 1: Date & Time Format ----------------------------------- */}
      <Card>
        <CardHeader>
          <CardTitle>{t('dataDisplay.dateTimeCard')}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-6 md:grid-cols-3">
          <div className="space-y-1.5">
            <Label>{t('dataDisplay.timezone')}</Label>
            <SearchableTimezoneSelect
              value={draft.timezone}
              onChange={(v) => setDraft({ ...draft, timezone: v })}
              options={timezones}
              placeholder={t('dataDisplay.placeholder.selectTimezone')}
            />
          </div>

          <div className="space-y-1.5">
            <Label>{t('dataDisplay.dateFormat')}</Label>
            <Select value={draft.date_format} onValueChange={(v) => setDraft({ ...draft, date_format: v })}>
              <SelectTrigger><SelectValue placeholder={t('dataDisplay.placeholder.selectDateFormat')} /></SelectTrigger>
              <SelectContent>
                {dateChoices.map((c) => (
                  <SelectItem key={c.value} value={c.value}>
                    <span className="font-mono mr-2">{c.value}</span>
                    <span className="text-muted-foreground text-xs">— {c.label}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>{t('dataDisplay.timeFormat')}</Label>
            <Select value={draft.time_format} onValueChange={(v) => setDraft({ ...draft, time_format: v })}>
              <SelectTrigger><SelectValue placeholder={t('dataDisplay.placeholder.selectTimeFormat')} /></SelectTrigger>
              <SelectContent>
                {timeChoices.map((c) => (
                  <SelectItem key={c.value} value={c.value}>
                    <span className="font-mono mr-2">{c.value}</span>
                    <span className="text-muted-foreground text-xs">— {c.label}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Card 2: Live Preview ----------------------------------------- */}
      <Card>
        <CardHeader>
          <CardTitle>{t('dataDisplay.livePreview')}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-3 text-sm">
          <div className="border rounded-md p-3">
            <div className="text-muted-foreground text-xs">{t('dataDisplay.date')}</div>
            <div className="font-mono text-base">{formatDateWith(now, draft.date_format, draft.timezone)}</div>
          </div>
          <div className="border rounded-md p-3">
            <div className="text-muted-foreground text-xs">{t('dataDisplay.time')}</div>
            <div className="font-mono text-base">{formatTimeWith(now, draft.time_format, draft.timezone)}</div>
          </div>
          <div className="border rounded-md p-3">
            <div className="text-muted-foreground text-xs">{t('dataDisplay.dateAndTime')}</div>
            <div className="font-mono text-base">{formatDateTimeWith(now, draft.date_format, draft.time_format, draft.timezone)}</div>
          </div>
        </CardContent>
      </Card>

      {canEdit && (
        <div className="flex justify-end pt-2">
          <Button onClick={onSave} disabled={saving}>
            {saving ? t('common.saving') : t('dataDisplay.savePreferences')}
          </Button>
        </div>
      )}
    </div>
    </fieldset>
  );
}
